import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Bell, CheckCheck } from "lucide-react";
import { api } from "@/lib/api";
import { fmtDate } from "@/components/ui-bits";

const DOT = { success: "bg-emerald-400", error: "bg-red-400", info: "bg-[#00F0FF]" };

export function NotificationBell({ align = "right" }) {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const panelRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/notifications/mine");
      setItems(data.notifications || []);
      setUnread(data.unread || 0);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onDoc = (e) => {
      if (btnRef.current?.contains(e.target) || panelRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const positionPanel = () => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const width = Math.min(320, window.innerWidth * 0.86);
    let left = align === "left" ? r.left : r.right - width;
    left = Math.max(8, Math.min(left, window.innerWidth - width - 8));
    setCoords({ top: r.bottom + 8, left });
  };

  const toggle = async () => {
    const next = !open;
    if (next) positionPanel();
    setOpen(next);
    if (next && unread > 0) {
      try { await api.post("/notifications/read-all"); } catch { /* ignore */ }
      setUnread(0);
    }
  };

  return (
    <div className="relative">
      <button
        ref={btnRef}
        onClick={toggle}
        data-testid="notification-bell"
        aria-label="Notifications"
        className="relative inline-flex items-center justify-center w-9 h-9 rounded-lg border border-white/10 bg-[#0A132A] text-[#00F0FF] hover:border-[#00F0FF]/40 transition-all"
      >
        <Bell size={17} />
        {unread > 0 && (
          <span data-testid="notification-badge" className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={panelRef}
          style={{ position: "fixed", top: coords.top, left: coords.left, width: "min(320px, 86vw)" }}
          className="glass-strong rounded-xl border border-white/10 shadow-2xl z-[999] overflow-hidden"
          data-testid="notification-panel"
        >
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
            <span className="font-display font-bold text-sm">Notifications</span>
            <CheckCheck size={15} className="text-[#94A3B8]" />
          </div>
          <div className="max-h-80 overflow-y-auto">
            {items.length === 0 && <div className="px-4 py-8 text-center text-sm text-[#475569]">No notifications yet</div>}
            {items.map((n) => (
              <div key={n.id} className="px-4 py-3 border-b border-white/5 hover:bg-white/[0.02]" data-testid="notification-item">
                <div className="flex items-start gap-2.5">
                  <span className={`w-2 h-2 rounded-full mt-1.5 flex-shrink-0 ${DOT[n.type] || DOT.info}`} />
                  <div className="min-w-0">
                    <div className="text-sm font-semibold">{n.title}</div>
                    <div className="text-xs text-[#94A3B8] mt-0.5 leading-relaxed">{n.message}</div>
                    <div className="text-[10px] text-[#475569] mt-1 uppercase tracking-wider">{fmtDate(n.created_at)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
